const STORAGE_KEYS = {
  utm_source: "vyvron_utm_source",
  utm_medium: "vyvron_utm_medium",
  utm_campaign: "vyvron_utm_campaign",
  utm_content: "vyvron_utm_content",
  landing_page: "vyvron_landing_page",
  first_touch_date: "vyvron_first_touch_date",
  traffic_type: "vyvron_traffic_type",
  lead_source: "vyvron_lead_source",
};

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];

function safeGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function safeSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
}

function deriveTrafficType(utmMedium) {
  const m = (utmMedium || "").toLowerCase();
  if (m.includes("paid")) return "Paid";
  if (m.includes("organic")) return "Organic";
  return "Unknown";
}

function deriveLeadSource(utmSource, referrer) {
  if (utmSource) return utmSource;
  if (referrer) {
    try { return new URL(referrer).hostname || "Direct"; } catch { return "Direct"; }
  }
  return "Direct";
}

export function captureTracking() {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const incoming = {};
  let hasNewUtm = false;
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) { incoming[k] = v; hasNewUtm = true; }
  });

  // First-touch UTM values: only overwrite when the visitor lands with new explicit UTM params.
  if (hasNewUtm) {
    UTM_KEYS.forEach((k) => {
      if (incoming[k]) safeSet(STORAGE_KEYS[k], incoming[k]);
    });
  }

  // Always record the most-recent landing page (useful for debugging).
  safeSet(STORAGE_KEYS.landing_page, window.location.href);

  // first_touch_date — set once, never overwrite.
  if (!safeGet(STORAGE_KEYS.first_touch_date)) {
    safeSet(STORAGE_KEYS.first_touch_date, new Date().toISOString());
  }

  const storedSource = safeGet(STORAGE_KEYS.utm_source);
  const storedMedium = safeGet(STORAGE_KEYS.utm_medium);

  // Derived fields refresh whenever new UTM lands; otherwise keep stored value.
  if (hasNewUtm || !safeGet(STORAGE_KEYS.lead_source)) {
    safeSet(STORAGE_KEYS.lead_source, deriveLeadSource(storedSource, document.referrer));
  }
  if (hasNewUtm || !safeGet(STORAGE_KEYS.traffic_type)) {
    safeSet(STORAGE_KEYS.traffic_type, deriveTrafficType(storedMedium));
  }

  // Debug logging is limited to local development builds only.
  if (import.meta.env.DEV) {
    try {
      // eslint-disable-next-line no-console
      console.log("[Vyvron Tracking] captured:", getTrackingFields());
    } catch { /* ignore */ }
  }
}

/**
 * Returns the captured tracking values, ready to be sent with a lead form.
 * Field names match the hidden-field convention for the future intake / email-capture form:
 *   Lead_Source, UTM_Source, UTM_Medium, UTM_Campaign, UTM_Content,
 *   Landing_Page, First_Touch_Date, Traffic_Type
 */
export function getTrackingFields() {
  const here = typeof window !== "undefined" ? window.location.href : "";
  const now = new Date().toISOString();
  return {
    Lead_Source: safeGet(STORAGE_KEYS.lead_source) || "Unknown",
    UTM_Source: safeGet(STORAGE_KEYS.utm_source) || "",
    UTM_Medium: safeGet(STORAGE_KEYS.utm_medium) || "",
    UTM_Campaign: safeGet(STORAGE_KEYS.utm_campaign) || "",
    UTM_Content: safeGet(STORAGE_KEYS.utm_content) || "",
    Landing_Page: safeGet(STORAGE_KEYS.landing_page) || here,
    First_Touch_Date: safeGet(STORAGE_KEYS.first_touch_date) || now,
    Traffic_Type: safeGet(STORAGE_KEYS.traffic_type) || "Unknown",
  };
}

/**
 * Renders the captured tracking values as hidden <input> elements.
 * Drop this inside any future <form> to forward attribution with the submission:
 *   <form>...<TrackingHiddenFields />...</form>
 * (Import from "./lib/tracking.jsx" if you split the JSX export into its own file.)
 */
export function getHiddenFieldEntries() {
  return Object.entries(getTrackingFields());
}
